/**
 * Collection credit hold recommendation types — customer-scoped advisory records only.
 *
 * Recommendations do not mutate credit limits, AR, orders, or authoritative holds.
 */

export const COLLECTION_CREDIT_HOLD_RECOMMENDATION_CODES = ['credit_hold_advisory_v1'] as const;

export type CollectionCreditHoldRecommendationCode =
  (typeof COLLECTION_CREDIT_HOLD_RECOMMENDATION_CODES)[number];

export const COLLECTION_CREDIT_HOLD_RECOMMENDATION_STATUSES = [
  'active',
  'dismissed',
  'expired',
] as const;

export type CollectionCreditHoldRecommendationStatus =
  (typeof COLLECTION_CREDIT_HOLD_RECOMMENDATION_STATUSES)[number];

export const COLLECTION_CREDIT_HOLD_EVALUATOR_VERSION = 'credit_hold_recommendation_v1' as const;

export const COLLECTION_CREDIT_HOLD_MANDATORY_CAVEAT =
  'credit_hold_advisory_no_auto_enforcement' as const;

export const COLLECTION_CREDIT_HOLD_SOURCE_FINGERPRINT_MAX_LENGTH = 128;

import type { CollectionCreditHoldSourceSnapshotV1 } from '@shared/collection/collection-credit-hold-recommendation-source-snapshot';

export type CollectionCreditHoldSourceSnapshot = CollectionCreditHoldSourceSnapshotV1;

export type CollectionCreditHoldRationalePayload = Record<string, unknown>;

export interface CollectionCreditHoldRecommendation {
  id: string;
  tenantId: string;
  externalEntityId: string;
  recommendationCode: CollectionCreditHoldRecommendationCode;
  ruleProfileId: string;
  ruleProfileVersion: string;
  evaluatorVersion: string;
  sourceSnapshot: CollectionCreditHoldSourceSnapshot;
  sourceFingerprint: string;
  rationaleCode: string;
  rationalePayload: CollectionCreditHoldRationalePayload;
  reasonCodes: string[];
  caveats: string[];
  status: CollectionCreditHoldRecommendationStatus;
  createdAt: string;
  expiredAt: string | null;
  dismissedAt: string | null;
  dismissedByActorId: string | null;
  dismissalReasonCode: string | null;
  supersedesRecommendationId: string | null;
}

export interface CreateCollectionCreditHoldRecommendationInput {
  tenantId: string;
  externalEntityId: string;
  recommendationCode: CollectionCreditHoldRecommendationCode;
  ruleProfileId: string;
  ruleProfileVersion: string;
  evaluatorVersion: string;
  sourceSnapshot: CollectionCreditHoldSourceSnapshot;
  sourceFingerprint: string;
  rationaleCode: string;
  rationalePayload: CollectionCreditHoldRationalePayload;
  reasonCodes: string[];
  caveats: string[];
  supersedesRecommendationId?: string | null;
}

export interface CollectionCreditHoldRecommendationEvaluationIdentity {
  tenantId: string;
  externalEntityId: string;
  recommendationCode: CollectionCreditHoldRecommendationCode;
  sourceFingerprint: string;
  ruleProfileVersion: string;
  evaluatorVersion: string;
}

export type CollectionCreditHoldRecommendationDismissLifecycleUpdate = {
  status: 'dismissed';
  dismissedAt: string;
  dismissedByActorId: string;
  dismissalReasonCode?: string | null;
};

export type CollectionCreditHoldRecommendationExpireLifecycleUpdate = {
  status: 'expired';
  expiredAt: string;
};

export type CollectionCreditHoldRecommendationLifecycleUpdate =
  | CollectionCreditHoldRecommendationDismissLifecycleUpdate
  | CollectionCreditHoldRecommendationExpireLifecycleUpdate;

export function isCollectionCreditHoldRecommendationCode(
  value: string
): value is CollectionCreditHoldRecommendationCode {
  return (COLLECTION_CREDIT_HOLD_RECOMMENDATION_CODES as readonly string[]).includes(value);
}

export function isCollectionCreditHoldRecommendationStatus(
  value: string
): value is CollectionCreditHoldRecommendationStatus {
  return (COLLECTION_CREDIT_HOLD_RECOMMENDATION_STATUSES as readonly string[]).includes(value);
}
